<template>
  <BCard no-body class="dashboard-graph w-100">
    <BCardBody>
      <div class="graphsection">
        <div class="header">
          <div class="title">
            <h5>Portfolio Value</h5>
          </div>
          <div class="controls rounded">
            <label>Period :</label>
            <div class="custom-dropdown" v-click-outside="closeDropdown">
              <button
                class="dropdown-button"
                @click="toggleDropdown"
                type="button"
              >
                {{ selectedPeriodText }}
                <span class="dropdown-arrow">▼</span>
              </button>
              <div v-if="isDropdownOpen" class="dropdown-menu-custom">
                <div class="dropdown-item-custom" @click="selectPeriod('max')">
                  Max
                </div>
                <div class="dropdown-item-custom" @click="selectPeriod('1m')">
                  1 month
                </div>
                <div class="dropdown-item-custom" @click="selectPeriod('3m')">
                  3 months
                </div>
                <div class="dropdown-item-custom" @click="selectPeriod('6m')">
                  6 months
                </div>
                <div class="dropdown-item-custom" @click="selectPeriod('1y')">
                  1 year
                </div>
                <div class="dropdown-item-custom" @click="selectPeriod('3y')">
                  3 years
                </div>
              </div>
            </div>
          </div>
        </div>
        <apexchart
          class="apex-charts"
          height="245"
          type="area"
          dir="ltr"
          :series="splineAreaChart.series"
          :options="splineAreaChart.chartOptions"
        ></apexchart>
        <div class="note">
          <p>
            Note:
            <span v-if="isMobile && !showFullNote">
              {{ longText.slice(0, 50) }}...
              <a href="#" @click.prevent="toggleNote">Read more</a>
            </span>
            <span v-if="isMobile && showFullNote">
              {{ longText }}
              <a href="#" @click.prevent="toggleNote">Show less</a>
            </span>
            <span v-if="!isMobile">
              {{ longText }}
            </span>
          </p>
        </div>
      </div>
    </BCardBody>
  </BCard>
</template>

<script>
import { ref, watch, onMounted, computed } from "vue";
import { useStore } from "vuex";

export default {
  name: "GraphSection",
  data() {
    return {
      viewPortfolioLink: `${process.env.VUE_APP_BASE_URL}/my-portfolio-review-pro/summary`,
      isDropdownOpen: false,
    };
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    selectPeriod(period) {
      this.selectedOption = period;
      this.isDropdownOpen = false;
      this.filterData();
    },
  },
  setup() {
    const store = useStore();
    const selectedOption = ref("max");
    const longText = ref(
      `Data from date of first download in Portfolio Review. Fresh investments or redemptions reflect when updated in the tool. Current values are based on latest NAVs for mutual funds and market prices for stocks & ETFs.`
    );
    const portfolioType = computed(() => store.getters.portfolioType);
    const holdingType = computed(() => store.getters.holdingType);
    const showFullNote = ref(false);

    const selectedPeriodText = computed(() => {
      const periodTexts = {
        max: "Max",
        "1m": "1 month",
        "3m": "3 months",
        "6m": "6 months",
        "1y": "1 year",
        "3y": "3 years",
      };
      return periodTexts[selectedOption.value] || "Max";
    });
    const splineAreaChart = ref({
      series: [],
      chartOptions: {
        chart: {
          type: "area",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        colors: ["#205AA6", "#21D19F"],
        xaxis: {
          type: "datetime",
          labels: {
            show: true,
            offsetX: 5,
            offsetY: 0,
            style: {
              colors: ["#012e40"],
            },
            formatter: function (value) {
              const date = new Date(value);
              if (
                selectedOption.value === "1m" ||
                selectedOption.value === "3m"
              ) {
                return date.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                });
              } else {
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  year: "2-digit",
                });
              }
            },
          },
        },
        yaxis: {
          opposite: true,
          labels: {
            style: {
              colors: ["#012e40"],
            },
            formatter: function (value) {
              if (value >= 10000000) {
                return (value / 10000000).toFixed(2) + "Cr";
              } else if (value >= 100000) {
                return (value / 100000).toFixed(2) + "L";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(2) + "K";
              } else {
                return value.toFixed(2);
              }
            },
          },
          axisBorder: {
            show: true,
            color: "#e6e6e9",
            offsetX: 0,
            offsetY: 0,
          },
          axisTicks: {
            show: true,
            borderType: "solid",
            color: "#e6e6e9",
            width: 6,
            offsetX: 0,
            offsetY: 0,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        grid: {
          borderColor: "#e6e6e9",
        },
        tooltip: {
          x: {
            formatter: function (value) {
              const date = new Date(value);
              return date.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              });
            },
          },
          y: {
            formatter: function (value) {
              if (value >= 10000000) {
                return (value / 10000000).toFixed(2) + "Cr";
              } else if (value >= 100000) {
                return (value / 100000).toFixed(2) + "L";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(2) + "K";
              } else {
                return value.toFixed(2);
              }
            },
          },
        },
      },
    });
    const isMobile = computed(() => window.innerWidth <= 767);
    const toggleNote = () => {
      showFullNote.value = !showFullNote.value;
    };
    const preprocessData = (data) => {
      const sortedData = data.sort(
        (a, b) => new Date(a.retrieval_date) - new Date(b.retrieval_date)
      );

      const totalPoints = 6;
      const interval = Math.floor((sortedData.length - 1) / (totalPoints - 1));

      const selectedPoints = [];
      selectedPoints.push(sortedData[0]);
      for (let i = 1; i < totalPoints - 1; i++) {
        selectedPoints.push(sortedData[i * interval]);
      }
      selectedPoints.push(sortedData[sortedData.length - 1]);

      let currentValues, investedValues;
      if (holdingType.value === "stocks") {
        currentValues = sortedData.map((item) =>
          parseFloat(item.stock_current_value || 0)
        );
        investedValues = sortedData.map((item) =>
          parseFloat(item.stock_invested_value || 0)
        );
      } else if (holdingType.value === "mutualfunds") {
        currentValues = sortedData.map((item) =>
          parseFloat(item.current_value || 0)
        );
        investedValues = sortedData.map((item) =>
          parseFloat(item.invested_value || 0)
        );
      } else {
        currentValues = sortedData.map((item) => {
          const mfValue = parseFloat(item.current_value || 0);
          const stockValue = parseFloat(item.stock_current_value || 0);
          return mfValue + stockValue;
        });
        investedValues = sortedData.map((item) => {
          const mfValue = parseFloat(item.invested_value || 0);
          const stockValue = parseFloat(item.stock_invested_value || 0);
          return mfValue + stockValue;
        });
      }
      return {
        graphDates: sortedData.map((item) => item.retrieval_date),
        currentValues,
        investedValues,
      };
    };
    const getGraphSection = (portfolioData) => {
      return portfolioType.value === "own"
        ? portfolioData?.ownPortfolio?.graphSection
        : portfolioData?.familyPortfolio?.graphSection;
    };

    const filterData = () => {
      const allData = getGraphSection(store.getters.myPortfolio) || [];
      let filteredData = [];

      let startDate;
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      switch (selectedOption.value) {
        case "1m":
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case "3m":
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case "6m":
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 6);
          break;
        case "1y":
          startDate = new Date();
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        case "3y":
          startDate = new Date();
          startDate.setFullYear(startDate.getFullYear() - 3);
          break;
        case "max":
        default:
          filteredData = allData;
          break;
      }

      if (startDate) {
        filteredData = allData.filter((item) => {
          const itemDate = new Date(item.retrieval_date);
          return itemDate >= startDate && itemDate <= yesterday;
        });
      } else {
        filteredData = allData.filter(
          (item) => new Date(item.retrieval_date) <= yesterday
        );
      }

      updateChart({
        [portfolioType.value + "Portfolio"]: { graphSection: filteredData },
      });
    };

    const updateChart = (portfolioData) => {
      const graphSection = getGraphSection(portfolioData) || [];
      if (graphSection.length === 0) {
        splineAreaChart.value.series = [];
        return;
      }
      const chartData = preprocessData(graphSection);
      splineAreaChart.value.series = [
        {
          name: "Current Value",
          data: chartData.currentValues.map((value, index) => ({
            x: new Date(chartData.graphDates[index]).getTime(),
            y: value,
          })),
        },
        {
          name: "Invested Value",
          data: chartData.investedValues.map((value, index) => ({
            x: new Date(chartData.graphDates[index]).getTime(),
            y: value,
          })),
        },
      ];
    };

    watch(
      () => store.getters.myPortfolio,
      (newPortfolio) => {
        if (getGraphSection(newPortfolio)) {
          filterData();
        }
      },
      { immediate: true }
    );

    watch(
      () => portfolioType.value,
      () => {
        filterData();
      }
    );

    watch(
      () => holdingType.value,
      () => {
        filterData();
      }
    );

    onMounted(() => {
      filterData();
    });

    return {
      splineAreaChart,
      selectedOption,
      filterData,
      portfolioType,
      holdingType,
      isMobile,
      toggleNote,
      longText,
      showFullNote,
      selectedPeriodText,
    };
  },
};
</script>

<style scoped>
.custom-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-button {
  background: transparent;
  border: none;
  color: #00a3d9;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  margin-top: 5px !important;
  transition: color 0.2s ease;
  padding: unset !important;
}

.dropdown-button:focus {
  outline: none;
}

.dropdown-arrow {
  font-size: 10px;
  transition: transform 0.2s ease;
}

.dropdown-menu-custom {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 120px;
  margin-top: 5px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item-custom {
  padding: 8px 16px;
  cursor: pointer;
  color: #333;
  font-size: 14px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.dropdown-item-custom:hover {
  background-color: #f8f9fa;
  color: #0d6efd;
}

@media only screen and (max-width: 515px) {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
}

@media only screen and (min-width: 516px) {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 10px;
  }
}

.dashboard-graph {
  background: linear-gradient(180deg, #f6fdff 0%, #e2f9ff 100%);
  border-radius: 5px;
}

.title {
  font-size: 1.2em;
  font-weight: bold;
}

.controls {
  display: flex;
  border: 1px solid grey;
  padding: 0px 5px;
}

.controls label {
  margin: 5px;
  font-size: 12px;
}

.toggle-button {
  margin-right: 10px;
  padding: 5px 10px;
  font-size: 1em;
  cursor: pointer;
}

.view-funds-link {
  font-size: 1em;
  text-decoration: none;
  color: #007bff;
}

.note {
  margin-top: 23px;
  font-size: 10px;
  color: #333;
}
.note p {
  margin: 0;
}
.note a {
  color: #007bff;
  text-decoration: none;
  cursor: pointer;
}
.note a:hover {
  text-decoration: underline;
}
</style>
